import React, { useEffect, useMemo, useState } from "react";
import { Images, Search, Download, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cardCls, Input, EmptyState } from "@/components/admin/ui";

export default function ReceiptGalleryCard({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/receipts").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [refreshKey]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((r) =>
      [r.description, r.category, r.venture_name, r.filename, r.date].some((f) => (f || "").toLowerCase().includes(s))
    );
  }, [items, q]);

  const fmt = (n) => "£" + Number(n || 0).toLocaleString("en-GB");
  const isImg = (ct) => (ct || "").startsWith("image/");

  const download = async (r) => {
    try {
      const res = await api.get(`/files/${r.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename || `receipt-${r.file_id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download receipt"); }
  };
  const open = async (r) => {
    try {
      const res = await api.get(`/files/${r.file_id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open receipt"); }
  };

  return (
    <div data-testid="receipt-gallery" className={`${cardCls} p-6 mb-6`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <Images size={20} className="text-bd-gold" />
          <h2 className="font-serif text-xl text-bd-ivory">Receipt gallery</h2>
          <span className="text-xs text-bd-ivory/40">{items.length}</span>
        </div>
        <div className="relative max-w-xs w-full">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-bd-ivory/30" />
          <Input data-testid="receipt-search" placeholder="Search receipts…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
      </div>

      {loading ? (
        <EmptyState>Loading receipts…</EmptyState>
      ) : items.length === 0 ? (
        <EmptyState>No receipts yet. Attach a receipt when recording an expense above.</EmptyState>
      ) : filtered.length === 0 ? (
        <EmptyState>No receipts match "{q}".</EmptyState>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((r) => (
            <div key={r.file_id} data-testid={`receipt-${r.file_id}`} className="border border-white/10 bg-bd-black/40 p-4 flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex-shrink-0 border border-white/10 flex items-center justify-center text-bd-gold">
                  {isImg(r.content_type) ? <ImageIcon size={16} /> : <FileText size={16} />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-bd-ivory truncate">{r.description || r.filename}</p>
                  <p className="text-xs text-bd-ivory/40 truncate">{r.category} · {r.venture_name}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-bd-ivory/50">
                <span>{r.date || "—"}</span>
                <span className="text-bd-ivory/80">{fmt(r.amount)}</span>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <button data-testid={`receipt-open-${r.file_id}`} onClick={() => open(r)} className="flex-1 text-center border border-white/15 text-bd-ivory/70 hover:border-bd-gold hover:text-bd-gold transition-colors py-2 text-[0.7rem] tracking-wider uppercase">View</button>
                <button data-testid={`receipt-download-${r.file_id}`} onClick={() => download(r)} title="Download" className="p-2 border border-white/15 text-bd-ivory/60 hover:border-bd-gold hover:text-bd-gold transition-colors"><Download size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
